import { faExternalLinkAlt, faRss, faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ChangeEvent, Dispatch, FormEvent, SetStateAction, useEffect, useReducer, useRef, useState } from 'react';
import { Alert, Badge, Button, ButtonGroup, Col, Container, Form, InputGroup, ListGroup, Modal, ProgressBar, Tab, Tabs } from 'react-bootstrap';
import { useFirebase } from '../../../contexts/FirebaseContext';
import { useDetectChanges } from '../../../hooks/useDetectChanges';
import { Copyable } from '../../components/Copyable/Copyable';
import { CountryFlag } from '../../components/CountryFlag/CountryFlag';
import { CSVParser } from '../../components/CSVParser/CSVParser';
import { ExtendedKeyboard, IExtendedKeyboard } from '../../components/ExtendedKeyboard/ExtendedKeyboard';
import { Footer } from '../../components/Footer/Footer';
import { FullscreenLoader } from '../../components/FullscreenLoader/FullscreenLoader';
import { LanguageCodeInput } from '../../components/LanguageCodeInput/LanguageCodeInput';
import { Plus } from '../../components/Plus/Plus';
import { SubtitleUploader } from '../../components/SubtitleUploader/SubtitleUploader';
import { Title } from '../../components/Title/Title';
import { Topbar } from '../../components/Topbar/Topbar';
import { WithTooltip } from '../../components/WithTooltip/WithTooltip';
import { formReducer, getLanguageName, getVideoDuration } from '../../utils/utils';
import { getPublishProgressLabel } from './publish-step.helpers';
import './Publish.scss';

const PublishComponent = () => {
  const i18nStartPattern = 'i18n';
  const genericLanguage = '*';
  const l = (name, language) => `${i18nStartPattern}.${language}.${name}`;
  const fieldsToUpdate = [
    ['title', ''],
    ['description', ['']],
    ['ts', []],
    ['xray', []],
  ];

  // prettier-ignore
  const specialCharacters: IExtendedKeyboard = [['shift', ['à', 'À'], ['á', 'Á'], ['è', 'È'], ['é', 'É'], ['ì', 'Ì'], ['í', 'Í'], ['ò', 'Ò'], ['ó', 'Ó'], ['ù', 'Ù'], ['ú', 'Ú'], ['ñ', 'Ñ'], ['¿', '¿'], ['¡', '¡']]];
  // prettier-ignore
  const russianKeyboard: IExtendedKeyboard = [
    [['ё', 'Ё'], null, null, ['№', '№'], null, null, null, null, null, null, null, null, null, null],
    [null, ['й', 'Й'], ['ц', 'Ц'], ['у', 'У'], ['к', 'К'], ['е', 'Е'], ['н', 'Н'], ['г', 'Г'], ['ш', 'Ш'], ['щ', 'Щ'], ['з', 'З'], ['х', 'Х'], ['ъ', 'Ъ'], null],
    [null, ['ф', 'Ф'], ['ы', 'Ы'], ['в', 'В'], ['а', 'А'], ['п', 'П'], ['р', 'Р'], ['о', 'О'], ['л', 'Л'], ['д', 'Д'], ['ж', 'Ж'], ['э', 'Э'], null, null],
    ['shift', null, ['я', 'Я'], ['ч', 'Ч'], ['с', 'С'], ['м', 'М'], ['и', 'И'], ['т', 'Т'], ['ь', 'Ь'], ['б', 'Б'], ['ю', 'Ю'], null, null, null],
  ];

  const { getPublishData, videoDataStorage, database, fv, currentUser, upsertHubspotProduct, getAwsGuid } = useFirebase();
  const detectChanges = useDetectChanges();
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [series, setSeries] = useState([]);
  const [formData, setFormData] = useReducer(formReducer, {
    // json: {},
    [l('title', 'it')]: '',
    [l('keywords', 'it')]: '',
    [l('description', 'it')]: [''],
    author: '',
    tags: [],
    [l('ts', 'it')]: [],
    [l('xray', 'it')]: [],
    introSeconds: 19,
    outroSeconds: 50,
    visibleFor: {
      odontoiatra: false,
      aso: false,
    },
  });
  const [activeTab, setActiveTab] = useState('it');
  const [languages, setLanguages] = useState(['it']);
  const [formErrors, setFormErrors] = useState<any>({});
  const [afterValidation, setAfterValidation] = useState(false);
  const [formErrorsModalOpen, setFormErrorsModalOpen] = useState(false);
  const [currentLanguageUploadIndex, setCurrentLanguageUploadIndex] = useState(-1);
  const [publishStep, setPublishStep] = useState(0);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showCheckDurationModal, setShowCheckDurationModal] = useState(false);
  const [checkDurationError, setCheckDurationError] = useState<string>(null);
  const [availableAudioLanguages, setAvailableAudioLanguages] = useState<string[]>(null);
  const [availableAudioLanguagesLoading, setAvailableAudioLanguagesLoading] = useState(false);
  const [originalLanguage, setOriginalLanguage] = useState(null);
  const [isLanguageCodeValid, setIsLanguageCodeValid] = useState(false);

  const [fetchGuid, setFetchGuid] = useState('');
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [documentExists, setDocumentExists] = useState(false);

  const [thumbnailUploadProgress, setThumbnailUploadProgress] = useState(0);
  const [thumbnailVerticalUploadProgress, setThumbnailVerticalUploadProgress] = useState(0);
  const [trailerUploadProgress, setTrailerUploadProgress] = useState(0);

  const [uploadError, setUploadError] = useState<string>(null);

  const [tsCsvParserOpen, setTsCsvParserOpen] = useState(false);
  const [xrayCsvParserOpen, setXrayCsvParserOpen] = useState(false);

  const subtitles = useRef<{ [p: string]: File }>({});
  const [subtitleModalOpen, setSubtitleModalOpen] = useState(false);

  const [selectedSeries, setSelectedSeries] = useState<[seriesId: string, seasonId: string, order: number][]>([]);

  const finalJson = useRef<any>({});

  const handleUploadTask = (guid: string, item: File, itemName: string, progressSetter?: Dispatch<SetStateAction<number>>, onCompleteOverride?: () => any) => {
    const storageRef = videoDataStorage.child(`video_data/${guid}/${itemName}`);
    const uploadTask = storageRef.put(item);
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        const progress = ((snapshot.bytesTransferred / snapshot.totalBytes) * 100) | 0;
        if (progressSetter) progressSetter(progress);
      },
      (error) => {
        console.error(error);
        setUploadError(`${error.code}: ${error.message}`);
      },
      () => {
        if (onCompleteOverride) onCompleteOverride();
        else setPublishStep((current) => current + 1);
      }
    );
  };

  const generateFinalJson = async () => {
    return new Promise<any>(async (resolve) => {
      const duration = await getVideoDuration(formData.json.egressEndpoints.MSS);
      const obj = {
        duration,
        created: fv.serverTimestamp(),
        filename: formData.json.srcVideo,
        hls: formData.json.egressEndpoints.HLS,
        hls_drm: formData.json.egressEndpoints.HLS_DRM,
        dash: formData.json.egressEndpoints.DASH,
        dash_drm: formData.json.egressEndpoints.DASH_DRM,
        mss: formData.json.egressEndpoints.MSS,
        mediaPackageResourceId: formData.json.mediaPackageResourceId,
        visible: false,
        author: null,
        tags: [],
        i18n: getFormDataI18n(false),
        introSeconds: formData.introSeconds,
        outroSeconds: formData.outroSeconds,
        lastEditBy: currentUser.uid,
        visibleFor: {
          odontoiatra: formData.visibleFor.odontoiatra,
          aso: formData.visibleFor.aso,
        },
        ts: [],
        xray: [],
        languages: languages,
        originalLanguage,
      };
      if (formData.author) {
        obj.author = database.doc(`authors/${formData.author}`);
      }
      if (formData.tags.length) {
        obj.tags = formData.tags.map((tagId) => database.doc(`tags/${tagId}`));
      }

      for (const lang of languages) {
        if (!obj.i18n[lang]) obj.i18n[lang] = {};
        const languageTs = obj.i18n[lang].ts;
        if (languageTs?.length) languageTs.sort((a, b) => a.at - b.at);
        const languageXray = obj.i18n[lang].xray;
        if (languageXray?.length) languageXray.sort((a, b) => a.to - b.to).sort((a, b) => a.from - b.from);
      }

      for (const prop of ['ts', 'xray']) {
        obj[prop] = obj.i18n[Object.keys(obj.i18n)[0]][prop];
        if (obj[prop]?.length) {
          for (const lang of languages) {
            const source = obj.i18n[lang][prop];
            if (!source?.length) continue;
            for (let i = 0; i < obj[prop].length; i++) {
              if (!obj[prop][i] || !source[i]) continue;
              if (!obj[prop][i].i18n) obj[prop][i].i18n = {};
              obj[prop][i].i18n[lang] = { description: source[i].description };
            }
          }
        }
      }

      for (const lang of languages) {
        delete obj.i18n[lang].ts;
        delete obj.i18n[lang].xray;

        if (obj.i18n[lang].keywords) {
          obj[`keywords_${lang}`] = [obj.i18n[lang].keywords, obj.i18n[lang].title, obj.i18n[lang].description.join(',')].join(',');
          delete obj.i18n[lang].keywords;
        }
      }

      finalJson.current = obj;
      resolve(obj);
    });
  };

  const publishVideo = (guid: string) => {
    return Promise.all([
      ...selectedSeries.map(([seriesId, seasonId, order]) =>
        database.collection(`series/${seriesId}/seasons/${seasonId}/episodes`).add({
          order,
          video: database.doc(`videos/${guid}`),
          visible: true,
        })
      ),
      database.doc(`videos/${guid}`).set(finalJson.current, { merge: true }),
    ]);
  };

  const createNotifications = async (guid: string) => {
    await Promise.all(languages.map((language) => database.collection('videoNotifications').add({ videoGuid: guid, language, scheduled: null, sent: false })));
  };

  const getFormDataI18n = (includeFiles = true): any => {
    return Object.entries(formData)
      .filter(([k]) => k.startsWith(i18nStartPattern))
      .filter(([k]) => {
        const [, lang] = k.split('.');
        return languages.includes(lang);
      })
      .reduce((acc, [k, v]) => {
        const [, lang, key] = k.split('.');
        if (!includeFiles && ['trailer', 'thumbnail', 'thumbnail_vertical'].includes(key)) return acc;
        if (!acc[lang]) {
          if (includeFiles) {
            acc[lang] = {
              title: '',
              keywords: '',
              trailer: null,
              thumbnail: null,
              thumbnail_vertical: null,
            };
          } else acc[lang] = { title: '', keywords: '' };
        }
        acc[lang][key] = v;
        return acc;
      }, {});
  };

  const appendToTitle = (language: string) => (key: string) => {
    setFormData({ name: l('title', language), value: formData[l('title', language)] + key });
    (document.querySelector(`#form-title-${language}`) as HTMLInputElement)?.focus();
  };

  useEffect(() => {
    getPublishData().then(({ authors, tags, series }) => {
      setTags(tags);
      setAuthors(authors);
      setSeries(series);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!publishStep || currentLanguageUploadIndex == -1) return;
    if (publishStep > 9) return;

    const guid = formData.json.guid;
    const currentLanguageUpload = languages[currentLanguageUploadIndex];
    (async () => {
      switch (publishStep) {
        case 1:
          handleUploadTask(guid, formData[l('thumbnail', currentLanguageUpload)], `thumbnail_${currentLanguageUpload}.webp`, setThumbnailUploadProgress);
          break;
        case 2:
          handleUploadTask(
            guid,
            formData[l('thumbnail_vertical', currentLanguageUpload)],
            `thumbnail_vertical_${currentLanguageUpload}.webp`,
            setThumbnailVerticalUploadProgress
          );
          break;
        case 3:
          handleUploadTask(guid, formData[l('trailer', currentLanguageUpload)], `trailer_${currentLanguageUpload}.webm`, setTrailerUploadProgress);
          break;
        case 4:
          setPublishStep((current) => current + 1);
          break;
        case 5:
          if (currentLanguageUploadIndex < languages.length - 1) {
            setCurrentLanguageUploadIndex((current) => current + 1);
            setPublishStep(1);
            return;
          }

          for (const sub of Object.values(subtitles.current)) {
            await new Promise<void>((resolve) => {
              handleUploadTask(guid, sub, `subs/${sub.name}`, null, resolve);
            });
          }

          setPublishStep((current) => current + 1);
          break;
        case 6:
          generateFinalJson()
            .then(() => setPublishStep((current) => current + 1))
            .catch((error) => {
              console.error(error);
              setUploadError(`${error?.code}: ${error?.message}`);
            });
          break;
        case 7:
          publishVideo(guid)
            .then(() => setPublishStep((current) => current + 1))
            .catch((error) => {
              console.error(error);
              setUploadError(`${error?.code}: ${error?.message}`);
            });
          break;
        case 8:
          upsertHubspotProduct(guid, 'video')
            .then(() => setPublishStep((current) => current + 1))
            .catch((error) => {
              console.error(error);
              setUploadError(`${error?.code}: ${error?.message}`);
            });
          break;
        case 9:
          createNotifications(guid)
            .then(() => setPublishStep((current) => current + 1))
            .catch((error) => {
              console.error(error);
              setUploadError(`${error?.code}: ${error?.message}`);
            });
      }
    })();
  }, [publishStep, currentLanguageUploadIndex]);

  useEffect(() => {
    if (!showCheckDurationModal) {
      setCheckDurationError(null);
      return;
    }

    getVideoDuration(formData.json.egressEndpoints.MSS).then((duration) => {
      if (!duration) {
        setCheckDurationError('MSS non valido, attendi qualche minuto o richiedi un nuovo render!');
        return;
      }
      setShowCheckDurationModal(false);
    });
  }, [showCheckDurationModal]);

  useEffect(() => {
    if (!formData?.json?.egressEndpoints?.HLS) {
      setAvailableAudioLanguages(null);
      return;
    }

    setAvailableAudioLanguagesLoading(true);
    fetch(formData.json.egressEndpoints.HLS)
      .then((res) => res.text())
      .then((manifest) => {
        const rows = manifest.split(/\r?\n/);
        const audioRows = rows.filter((row) => row.startsWith('#EXT-X-MEDIA:TYPE=AUDIO'));
        const audioLanguages = audioRows.map((row) => {
          const [, language] = row.match(/NAME="(.+?)"/);
          return language;
        });
        const availableAudioLanguages = Array.from(new Set(audioLanguages));
        setAvailableAudioLanguages(availableAudioLanguages);
      })
      .catch(console.error)
      .finally(() => {
        setAvailableAudioLanguagesLoading(false);
      });
  }, [formData?.json?.egressEndpoints?.HLS]);

  const checkErrors = () => {
    const mandatoryKeys = ['title', 'trailer', 'thumbnail', 'thumbnail_vertical'];
    const i18nValues = getFormDataI18n();
    const errors = {};
    for (const [lang, entries] of Object.entries(i18nValues)) {
      for (const [key, value] of Object.entries(entries)) {
        if (
          (mandatoryKeys.includes(key) && !value) ||
          (key == 'ts' && !value.every((ts) => ts.at && ts.description)) ||
          (key == 'xray' && !value.every((xray) => xray.from && xray.to && xray.description))
        ) {
          if (!errors[lang]) errors[lang] = [];
          errors[lang].push(key);
        }
      }
    }

    const areAllTsEqual = Object.values(i18nValues)
      .map((lang: any) => (lang.ts || []).length)
      .every((item, _, arr) => item == arr[0]);
    if (!areAllTsEqual) {
      if (!errors[genericLanguage]) errors[genericLanguage] = [];
      errors[genericLanguage].push('I timestamp non sono la stessa quantità in tutte le lingue');
    }
    const areAllXrayEqual = Object.values(i18nValues)
      .map((lang: any) => (lang.xray || []).length)
      .every((item, _, arr) => item == arr[0]);
    if (!areAllXrayEqual) {
      if (!errors[genericLanguage]) errors[genericLanguage] = [];
      errors[genericLanguage].push('Gli strumenti non sono la stessa quantità in tutte le lingue');
    }
    if (!originalLanguage || !isLanguageCodeValid) {
      if (!errors[genericLanguage]) errors[genericLanguage] = [];
      errors[genericLanguage].push('La lingua originale del video non è stata impostata');
    }
    setFormErrors(errors);
    if (Object.keys(errors).length) setFormErrorsModalOpen(true);
    else setAfterValidation(true);
  };

  useEffect(() => {
    if (!afterValidation) return;
    setShowPublishModal(true);
    setCurrentLanguageUploadIndex(0);
    setPublishStep(1);
  }, [afterValidation]);

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    checkErrors();
  };

  if (loading) return <FullscreenLoader />;

  return (
    <>
      <Title>Pubblica video</Title>

      <div style={{ marginBottom: '72px' }}>
        <div style={{ backgroundColor: '#141414', position: 'fixed', top: '0', left: '0', right: '0', zIndex: 100 }}>
          <Topbar />
        </div>
      </div>

      <Container className={'my-5'}>
        <Form onSubmit={onSubmit}>
          <div className={'d-flex align-items-baseline justify-content-between'}>
            <div className={'display-4'}>Pubblica video</div>
            {!!formData?.json && (
              <Button
                variant={'warning'}
                type={'submit'}
                disabled={
                  !formData.json.guid ||
                  !formData.json.srcVideo ||
                  !formData.json.egressEndpoints?.MSS ||
                  !formData.json.egressEndpoints?.HLS ||
                  !formData.json.egressEndpoints?.HLS_DRM ||
                  !formData.json.egressEndpoints?.DASH ||
                  !formData.json.egressEndpoints?.DASH_DRM ||
                  !formData.json.mediaPackageResourceId ||
                  !formData.author
                }
              >
                <FontAwesomeIcon icon={faRss} className={'mr-2'} />
                Pubblica
              </Button>
            )}
          </div>

          <div className={'mt-3 d-flex justify-content-center'}>
            <InputGroup size={'lg'} style={{ width: '100%', maxWidth: '750px' }}>
              <InputGroup.Prepend>
                <Button variant={!!formData?.json ? 'success' : 'danger'} style={{ pointerEvents: 'none' }}>
                  GUID:
                </Button>
              </InputGroup.Prepend>
              <Form.Control
                isValid={!!formData?.json}
                isInvalid={!formData?.json && fetchError}
                placeholder={'Inserisci il GUID del video...'}
                disabled={fetchLoading || formData?.json}
                value={fetchGuid}
                onKeyDown={(e) => {
                  if (e.key == 'Enter') {
                    e.preventDefault();
                    if (fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)) return false;
                    (document.querySelector('#guid-submit-button') as HTMLButtonElement)?.click();
                  }
                }}
                onChange={(e) => {
                  setFetchError(false);
                  setFetchGuid(e.target.value.trim());
                }}
              />
              {!!fetchGuid && !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid) && (
                <Form.Text style={{ position: 'absolute', top: '42px', left: '88px' }} className={'text-warning'}>
                  Il GUID inserito non è nel formato corretto.
                </Form.Text>
              )}
              {!formData?.json && (
                <InputGroup.Append>
                  <Button
                    disabled={fetchLoading || !fetchGuid || !/^\s*[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\s*$/.test(fetchGuid)}
                    variant={'primary'}
                    id={'guid-submit-button'}
                    onClick={() => {
                      setFetchLoading(true);
                      setFetchError(false);
                      getAwsGuid(fetchGuid).then((res) => {
                        if (!res) {
                          setFetchError(true);
                          setFetchLoading(false);
                        } else {
                          setFormData({ name: 'json', value: res });
                          setShowCheckDurationModal(true);
                          database
                            .doc(`videos/${fetchGuid}`)
                            .get()
                            .then((ref) => setDocumentExists(ref.exists));
                        }
                      });
                    }}
                  >
                    {fetchLoading ? <>Attendi&hellip;</> : <>Ottieni dati&hellip;</>}
                  </Button>
                </InputGroup.Append>
              )}
            </InputGroup>
          </div>
          {!formData?.json && !fetchError && (
            <div className={'mt-3 d-flex justify-content-center'}>
              <Alert variant={'info'} className={'mb-0 text-center'} style={{ width: '100%', maxWidth: '750px' }}>
                Incolla il GUID ricevuto dalla mail di AWS per cominciare.
              </Alert>
            </div>
          )}
          {!!fetchError && (
            <div className={'mt-3 d-flex justify-content-center'}>
              <Alert variant={'danger'} className={'mb-0 text-center'} style={{ width: '100%', maxWidth: '750px' }}>
                Errore nell'ottenimento delle informazioni richieste, verifica la correttezza del GUID.
              </Alert>
            </div>
          )}

          {!!formData?.json && (
            <div className={'d-flex mt-4'}>
              <div className={'d-flex flex-column flex-grow-1 w-50'}>
                <fieldset className={'mb-3 py-1 px-3 border rounded border-secondary'}>
                  <legend className={'text-center h6'}>Metadata</legend>
                  <div>
                    <strong>Video selezionato:</strong> <span>{formData.json.srcVideo}</span>
                  </div>
                  <div className={'mb-2'}>
                    <strong>Audio disponibili:</strong>
                    {availableAudioLanguagesLoading ? (
                      <span className={'ml-1'}>Caricamento&hellip;</span>
                    ) : (
                      <>
                        {!availableAudioLanguages?.length && (
                          <Badge className={'ml-1'} variant={'danger'}>
                            Errore!
                          </Badge>
                        )}
                        {availableAudioLanguages &&
                          availableAudioLanguages.map((language) => (
                            <CountryFlag variant={'success'} className={'ml-1'} key={language} language={language} withTooltip={true} />
                          ))}
                      </>
                    )}
                  </div>
                  {documentExists && (
                    <Alert className={'mb-2 ' + (!showCheckDurationModal ? 'animated-entrance' : '')} variant={'danger'}>
                      <strong>Attenzione!</strong> Esiste già un video su database corrispondente a questo GUID! Procedere con la pubblicazione unirà il nuovo
                      video a quello attualmente presente e potrebbe provocare risultati inattesi.
                    </Alert>
                  )}
                </fieldset>

                <fieldset className={'mb-3 py-1 px-3 border rounded border-secondary'}>
                  <legend className={'text-center h6'}>Campi comuni a tutte le lingue</legend>
                  <Form.Group>
                    <LanguageCodeInput onChange={(e) => setOriginalLanguage(e)} onErrorChange={(e) => setIsLanguageCodeValid(!e)} />
                    <Form.Label className={'d-flex justify-content-between align-items-baseline'}>
                      <div>Autore *</div>
                      {/*<small>
                    <a href={'#'} onClick={(e) => e.preventDefault()}>
                      &#65291;Autore
                    </a>
                  </small>*/}
                    </Form.Label>
                    <Form.Control as={'select'} defaultValue={''} onChange={(e) => setFormData({ name: 'author', value: e.target.value })}>
                      <option value={''} disabled>
                        Seleziona...
                      </option>
                      {authors.map((author) => (
                        <option key={author.id} value={author.id}>
                          {author.name}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>

                  <Form.Row>
                    <Col>
                      <Form.Group>
                        <Form.Label>Secondi di intro *</Form.Label>
                        <Form.Control
                          required
                          type={'number'}
                          value={formData.introSeconds}
                          min={0}
                          onChange={(e) => setFormData({ name: 'introSeconds', value: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group>
                        <Form.Label>Secondi di outro *</Form.Label>
                        <Form.Control
                          required
                          type={'number'}
                          value={formData.outroSeconds}
                          min={0}
                          onChange={(e) => setFormData({ name: 'outroSeconds', value: e.target.value })}
                        />
                      </Form.Group>
                    </Col>
                  </Form.Row>

                  <Form.Group>
                    <Form.Label className={'d-flex justify-content-between align-items-baseline'}>
                      <div>
                        Tags <small className={'text-muted'}>(CTRL+Click per selezione multipla o per de-selezionare)</small>
                      </div>
                      {/*<small>
                    <a href={'#'} onClick={(e) => e.preventDefault()}>
                      &#65291;Tag
                    </a>
                  </small>*/}
                    </Form.Label>
                    <Form.Control
                      as={'select'}
                      multiple
                      style={{ height: '200px' }}
                      onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                        setFormData({ name: 'tags', value: Array.from(e.target.selectedOptions).map((opt) => opt.value) })
                      }
                    >
                      {tags.map((tag) => (
                        <option key={tag.id} value={tag.id}>
                          {tag.label} {tag.showAsSeries && '[CATEGORIA]'}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Text className={'text-muted'}>
                      <span className={'mr-1'}>Tag selezionati: ({formData.tags.length})</span>
                      <strong>
                        {!formData.tags.length ? 'Nessun tag selezionato.' : formData.tags.map((tagId) => tags.find((tag) => tag.id == tagId).label).join(', ')}
                      </strong>
                    </Form.Text>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className={'d-flex justify-content-between align-items-baseline'}>
                      <div>
                        Serie <small className={'text-muted'}>(CTRL+Click per selezione multipla o per de-selezionare)</small>
                      </div>
                    </Form.Label>
                    <Form.Control
                      as={'select'}
                      multiple
                      onChange={(e: ChangeEvent<HTMLSelectElement>) => {
                        const selectedOpts = Array.from(e.target.selectedOptions).map((opt) => opt.value);
                        setSelectedSeries(
                          selectedOpts.map((id) => {
                            const seriesMatch = series.find((s) => s.id == id);
                            return [id, seriesMatch.seasonId, (seriesMatch.episodes + 1) * 10];
                          })
                        );
                      }}
                    >
                      {series.map((serie) => (
                        <option key={serie.id} value={serie.id}>
                          {serie.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Text className={'text-muted'}>
                      <span className={'mr-1'}>Serie selezionate: ({selectedSeries.length})</span>
                      <strong>
                        {!selectedSeries.length
                          ? 'Nessuna serie selezionata.'
                          : selectedSeries.map(([seriesId]) => series.find((serie) => serie.id == seriesId).name).join(', ')}
                      </strong>
                    </Form.Text>
                  </Form.Group>

                  <Form.Group>
                    <Form.Label className={'mb-0'}>Visibile per:</Form.Label>
                    <Form.Check
                      className={'ml-2'}
                      type={'checkbox'}
                      id={'chkOdontoiatra'}
                      inline
                      label={'Odontoiatra'}
                      checked={formData.visibleFor.odontoiatra}
                      onChange={(e) => setFormData({ name: 'visibleFor', value: { ...formData.visibleFor, odontoiatra: e.target.checked } })}
                    />

                    <Form.Check
                      type={'checkbox'}
                      id={'chkAso'}
                      inline
                      label={'ASO'}
                      checked={formData.visibleFor.aso}
                      onChange={(e) => setFormData({ name: 'visibleFor', value: { ...formData.visibleFor, aso: e.target.checked } })}
                    />
                    <Form.Text className={'text-muted'}>
                      Questa impostazione non renderà il video pubblico, ma ne limiterà la visibilità una volta abilitato in "Gestione contenuti"
                    </Form.Text>
                  </Form.Group>
                </fieldset>

                {/*<JSONInput
                locale={locale}
                className={'flex-grow-1'}
                placeholder={{
                  Step1: 'Incollare qui il JSON ricevuto da AWS e non applicare nessuna modifica',
                  Step2: 'Compilare il form a fianco, i campi contrassegnati da asterisco sono obbligatori',
                  Step3: 'Cliccare sul pulsante [Pubblica], se è disabilitato significa che qualche parte del form non è compilata in modo corretto',
                  Attenzione:
                    'Una volta pubblicato il video NON sarà visible al pubblico. Per renderlo visibile, sarà da attivare dalla sezione Gestione contenuti',
                }}
                onChange={(e) => setFormData({ name: 'json', value: e.jsObject || {} })}
              />*/}

                {!!availableAudioLanguages?.length && (
                  <fieldset className={'mb-3 py-1 px-3 border rounded border-secondary'}>
                    <legend className={'text-center h6'}>Test video</legend>
                    <div className={'pb-2'}>
                      <Alert variant={'warning'} className={'small'}>
                        <strong>Attenzione!</strong> Il player esterno potrebbe mostrare tracce audio duplicate: questo problema non è presente sul player
                        ufficiale.
                      </Alert>
                      <Button
                        block
                        variant={'dark'}
                        onClick={() => {
                          window.open(
                            `https://bitmovin.com/demos/stream-test?format=dash&manifest=${encodeURIComponent(formData.json.egressEndpoints.DASH)}`,
                            '_blank'
                          );
                        }}
                      >
                        Prova su player esterno
                        <FontAwesomeIcon icon={faExternalLinkAlt} className={'ml-2'} />
                      </Button>
                    </div>
                  </fieldset>
                )}
              </div>
              <div className={'flex-grow-1 ml-3 w-50'}>
                <fieldset className="mb-3 py-1 px-3 border rounded border-secondary">
                  <legend className="text-center h6">Sottotitoli</legend>
                  <div className="pb-2">
                    {!!Object.keys(subtitles.current).length && (
                      <ListGroup className={'mb-2'}>
                        {Object.entries(subtitles.current).map(([languageCode, file]) => (
                          <ListGroup.Item variant={'dark'} className={'dark-list-item'} key={languageCode}>
                            <div className={'d-flex align-items-center'}>
                              <CountryFlag language={languageCode} />
                              <span className={'ml-1'}>{file.name}</span>
                              <ButtonGroup size={'sm'} className={'ml-auto'}>
                                <WithTooltip text={'Apri'} big={false}>
                                  <Button
                                    variant={'primary'}
                                    onClick={() => {
                                      const objectUrl = URL.createObjectURL(file);
                                      window.open(objectUrl, '_blank');
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faExternalLinkAlt} fixedWidth={true} />
                                  </Button>
                                </WithTooltip>
                                <WithTooltip text={'Elimina'} big={false}>
                                  <Button
                                    variant={'danger'}
                                    onClick={() => {
                                      delete subtitles.current[languageCode];
                                      detectChanges();
                                    }}
                                  >
                                    <FontAwesomeIcon icon={faTimes} fixedWidth={true} />
                                  </Button>
                                </WithTooltip>
                              </ButtonGroup>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                    <Button block variant={'primary'} onClick={() => setSubtitleModalOpen(true)}>
                      <Plus /> Aggiungi traccia sottotitoli
                    </Button>
                  </div>
                </fieldset>

                <Tabs activeKey={activeTab} onSelect={setActiveTab} className={'mt-3'}>
                  <Tab disabled={true} title={'Lingue:'} eventKey={'__'} />
                  {languages.map((language) => (
                    <Tab title={<CountryFlag language={language} />} eventKey={language} key={language}>
                      <Button
                        variant={'danger'}
                        size={'sm'}
                        className={'mt-3'}
                        onClick={() => {
                          setLanguages(languages.filter((l) => l != language));
                          setActiveTab('add');
                        }}
                      >
                        Rimuovi lingua
                      </Button>

                      <Form.Group className={'mt-3'}>
                        <Form.Control
                          placeholder={'Titolo *'}
                          size={'lg'}
                          isInvalid={!formData[l('title', language)]}
                          value={formData[l('title', language)]}
                          id={`form-title-${language}`}
                          onChange={(e) => setFormData({ name: l('title', language), value: e.target.value })}
                        />
                        <Form.Label className={'mb-0 w-100'}>
                          <ExtendedKeyboard keyboard={specialCharacters} onClick={appendToTitle(language)} />
                        </Form.Label>
                      </Form.Group>

                      {language == 'ru' && (
                        <div className={'mb-2'}>
                          <ExtendedKeyboard keyboard={russianKeyboard} onClick={appendToTitle(language)} />
                        </div>
                      )}

                      <Form.Group>
                        <div>Descrizione</div>
                        {formData[l('description', language)].map((segment, i) => (
                          <div key={i.toString()} className={'mb-2'}>
                            <InputGroup>
                              <Form.Control
                                as={'textarea'}
                                value={segment}
                                onChange={(e) => {
                                  formData[l('description', language)][i] = e.target.value;
                                  detectChanges();
                                }}
                              />
                              <InputGroup.Append>
                                <Button
                                  variant={'danger'}
                                  onClick={() => {
                                    formData[l('description', language)].splice(i, 1);
                                    detectChanges();
                                  }}
                                >
                                  <FontAwesomeIcon icon={faTimes} />
                                </Button>
                              </InputGroup.Append>
                            </InputGroup>
                          </div>
                        ))}
                        <Button
                          variant={'outline-primary'}
                          size={'sm'}
                          onClick={() => {
                            formData[l('description', language)].push('');
                            detectChanges();
                          }}
                        >
                          <Plus /> Aggiungi paragrafo
                        </Button>
                      </Form.Group>

                      <Form.Group>
                        <div>Keywords</div>
                        <Form.Control
                          as={'textarea'}
                          value={formData[l('keywords', language)]}
                          onChange={(e) => {
                            setFormData({ name: l('keywords', language), value: e.target.value });
                          }}
                        />
                        <Form.Text className={'text-muted'}>
                          Titolo e descrizione del video verranno aggiunti automaticamente alle keywords dopo la pubblicazione per consentire la ricerca
                          multi-lingua.
                        </Form.Text>
                      </Form.Group>

                      <Form.Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>
                              Thumbnail * <small className={'text-muted'}>(WEBP)</small>
                            </Form.Label>
                            <Form.File accept={'image/webp'} onChange={(e) => setFormData({ name: l('thumbnail', language), value: e.target.files[0] })} />
                          </Form.Group>
                        </Col>

                        <Col>
                          <Form.Group>
                            <Form.Label>
                              Thumbnail verticale * <small className={'text-muted'}>(WEBP)</small>
                            </Form.Label>
                            <Form.File
                              accept={'image/webp'}
                              onChange={(e) => setFormData({ name: l('thumbnail_vertical', language), value: e.target.files[0] })}
                            />
                          </Form.Group>
                        </Col>
                      </Form.Row>

                      <Form.Row>
                        <Col>
                          <Form.Group>
                            <Form.Label>
                              Trailer * <small className={'text-muted'}>(WEBM)</small>
                            </Form.Label>
                            <Form.File accept={'video/webm'} onChange={(e) => setFormData({ name: l('trailer', language), value: e.target.files[0] })} />
                          </Form.Group>
                        </Col>

                        <Col></Col>
                      </Form.Row>

                      <Form.Group>
                        <Form.Label>Fasi/timestamps ({formData[l('ts', language)].length})</Form.Label>
                        <Alert variant={'danger'} className={'small'}>
                          <strong>Attenzione!</strong> I tempi di seguito devono corrispondere in tutte le lingue inserite, la sincronizzazione va fatta
                          manualmente manuale
                        </Alert>
                        {formData[l('ts', language)].map((ts, i) => (
                          <Form.Row key={i.toString()} className={'mb-2'}>
                            <Col xs={3}>
                              <Form.Control
                                placeholder={'Secondi *'}
                                isInvalid={!ts.at}
                                isValid={!!ts.at}
                                value={ts.at}
                                onChange={(e) => {
                                  ts.at = e.target.value;
                                  detectChanges();
                                }}
                              />
                            </Col>
                            <Col xs={8}>
                              <Form.Control
                                placeholder={'Descrizione *'}
                                isInvalid={!ts.description}
                                isValid={!!ts.description}
                                value={ts.description}
                                onChange={(e) => {
                                  ts.description = e.target.value;
                                  detectChanges();
                                }}
                              />
                            </Col>
                            <Col xs={1}>
                              <Button
                                variant={'danger'}
                                onClick={() => {
                                  formData[l('ts', language)].splice(i, 1);
                                  detectChanges();
                                }}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </Col>
                          </Form.Row>
                        ))}
                        <div>
                          <Button className={'mr-2'} variant={'primary'} size={'sm'} onClick={() => setTsCsvParserOpen(true)}>
                            &#x21e7; Upload CSV
                          </Button>
                          <Button
                            variant={'outline-primary'}
                            size={'sm'}
                            onClick={() => {
                              formData[l('ts', language)].push({ at: '', description: '' });
                              detectChanges();
                            }}
                          >
                            <Plus /> Aggiungi timestamp
                          </Button>
                        </div>
                      </Form.Group>

                      <Form.Group>
                        <Form.Label>Strumentazione ({formData[l('xray', language)].length})</Form.Label>
                        <Alert variant={'danger'} className={'small'}>
                          <strong>Attenzione!</strong> I tempi di seguito devono corrispondere in tutte le lingue inserite, la sincronizzazione va fatta
                          manualmente manuale
                        </Alert>
                        {formData[l('xray', language)].map((xray, i) => (
                          <Form.Row key={i.toString()} className={'mb-2'}>
                            <Col xs={2}>
                              <Form.Control
                                placeholder={'Da *'}
                                isInvalid={!xray.from}
                                isValid={!!xray.from}
                                value={xray.from}
                                onChange={(e) => {
                                  xray.from = e.target.value;
                                  detectChanges();
                                }}
                              />
                            </Col>
                            <Col xs={2}>
                              <Form.Control
                                placeholder={'A *'}
                                isInvalid={!xray.to}
                                isValid={!!xray.to}
                                value={xray.to}
                                onChange={(e) => {
                                  xray.to = e.target.value;
                                  detectChanges();
                                }}
                              />
                            </Col>
                            <Col xs={7}>
                              <Form.Control
                                placeholder={'Descrizione *'}
                                isInvalid={!xray.description}
                                isValid={!!xray.description}
                                value={xray.description}
                                onChange={(e) => {
                                  xray.description = e.target.value;
                                  detectChanges();
                                }}
                              />
                            </Col>
                            <Col xs={1}>
                              <Button
                                variant={'danger'}
                                onClick={() => {
                                  formData[l('xray', language)].splice(i, 1);
                                  detectChanges();
                                }}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </Col>
                          </Form.Row>
                        ))}
                        <div>
                          <Button className={'mr-2'} variant={'primary'} size={'sm'} onClick={() => setXrayCsvParserOpen(true)}>
                            &#x21e7; Upload CSV
                          </Button>
                          <Button
                            variant={'outline-primary'}
                            size={'sm'}
                            onClick={() => {
                              formData[l('xray', language)].push({ from: '', to: '', description: '' });
                              detectChanges();
                            }}
                          >
                            <Plus /> Aggiungi strumentazione
                          </Button>
                        </div>
                      </Form.Group>

                      <CSVParser
                        show={language == activeTab && tsCsvParserOpen}
                        onHide={() => setTsCsvParserOpen(false)}
                        parser={'ts'}
                        onRowsReady={(rows) => {
                          formData[l('ts', language)].push(...rows);
                          detectChanges();
                        }}
                      />
                      <CSVParser
                        show={language == activeTab && xrayCsvParserOpen}
                        onHide={() => setXrayCsvParserOpen(false)}
                        parser={'xray'}
                        onRowsReady={(rows) => {
                          formData[l('xray', language)].push(...rows);
                          detectChanges();
                        }}
                      />
                    </Tab>
                  ))}
                  <Tab title={<>Aggiungi lingua&hellip;</>} eventKey={'add'}>
                    <div className={'mt-3'}>
                      {['it', 'en', 'es', 'fr', 'ru'].map((lang) => (
                        <Button
                          variant={'dark'}
                          disabled={languages.includes(lang)}
                          key={lang}
                          className={'mr-1'}
                          style={{ filter: languages.includes(lang) ? 'grayscale(1)' : null }}
                          onClick={() => {
                            fieldsToUpdate.forEach(([field, defaultValue]) => {
                              setFormData({ name: l(field, lang), value: defaultValue });
                            });
                            setLanguages([...languages, lang]);
                            setActiveTab(lang);
                          }}
                        >
                          <CountryFlag language={lang} />
                          <span className={'ml-1'}>{getLanguageName(lang)}</span>
                        </Button>
                      ))}
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Form>
      </Container>

      <Footer />

      <Modal show={showPublishModal} onHide={() => setShowPublishModal(false)} backdrop={!!uploadError ? true : 'static'} keyboard={false}>
        <Modal.Header closeButton={!!uploadError}>
          <Modal.Title>Processo di pubblicazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {publishStep < 5 && (
            <>
              <div>
                <strong>
                  Lingua in gestione: <CountryFlag language={languages[currentLanguageUploadIndex]} />
                </strong>
              </div>

              <div className={'mt-3'}>
                <div>Upload thumbnail&hellip;</div>
                <ProgressBar
                  variant={thumbnailUploadProgress < 100 ? 'primary' : 'success'}
                  animated={thumbnailUploadProgress < 100}
                  now={thumbnailUploadProgress}
                  label={`${thumbnailUploadProgress}%`}
                />
              </div>

              {publishStep >= 2 && (
                <div className={'mt-3'}>
                  <div>Upload thumbnail verticale&hellip;</div>
                  <ProgressBar
                    variant={thumbnailVerticalUploadProgress < 100 ? 'primary' : 'success'}
                    animated={thumbnailVerticalUploadProgress < 100}
                    now={thumbnailVerticalUploadProgress}
                    label={`${thumbnailVerticalUploadProgress}%`}
                  />
                </div>
              )}

              {publishStep >= 3 && (
                <div className={'mt-3'}>
                  <div>Upload trailer&hellip;</div>
                  <ProgressBar
                    variant={trailerUploadProgress < 100 ? 'primary' : 'success'}
                    animated={trailerUploadProgress < 100}
                    now={trailerUploadProgress}
                    label={`${trailerUploadProgress}%`}
                  />
                </div>
              )}
            </>
          )}

          {publishStep >= 5 && (
            <div className={'mt-1'}>
              <div>Caricamento sottotitoli&hellip;</div>
            </div>
          )}

          {publishStep >= 6 && (
            <div className={'mt-3'}>
              <div>Generazione dati video&hellip;</div>
            </div>
          )}

          {publishStep >= 7 && (
            <div className={'mt-3'}>
              <div>{getPublishProgressLabel(7)}</div>
            </div>
          )}

          {publishStep >= 8 && (
            <div className={'mt-3'}>
              <div>{getPublishProgressLabel(8)}</div>
            </div>
          )}

          {publishStep >= 9 && (
            <>
              <Alert className={'flex-grow-1 text-center mt-3'} variant={'success'}>
                L'operazione è stata completata con successo!
              </Alert>
              <div className={'mt-3'}>
                L'ID del video è: <Copyable text={formData.json.guid} />
              </div>
            </>
          )}

          {publishStep < 8 && (
            <Alert className={'flex-grow-1 text-center mt-3'} variant={'warning'}>
              Non abbandonare la pagina mentre il processo è in corso!
            </Alert>
          )}

          {!!uploadError && (
            <Alert className={'flex-grow-1 text-center mt-3'} variant={'danger'}>
              {uploadError}
            </Alert>
          )}
        </Modal.Body>
        {publishStep >= 8 && (
          <Modal.Footer>
            <Button
              variant={'primary'}
              onClick={() => {
                setShowPublishModal(false);
                window.location.reload();
              }}
            >
              Chiudi
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal show={showCheckDurationModal} onHide={() => setShowCheckDurationModal(false)} backdrop={'static'} centered keyboard={false}>
        <Modal.Header>
          <Modal.Title>Controllo video&hellip;</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!checkDurationError && <p>Attendere&hellip;</p>}
          {!!checkDurationError && (
            <Alert variant={'danger'} className={'mb-0'}>
              {checkDurationError}
            </Alert>
          )}
        </Modal.Body>
        {!!checkDurationError && (
          <Modal.Footer>
            <Button variant={'secondary'} onClick={() => window.location.reload()}>
              Ricarica
            </Button>
          </Modal.Footer>
        )}
      </Modal>

      <Modal show={formErrorsModalOpen} onHide={() => setFormErrorsModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Errori di compilazione</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>I seguenti campi non risultano compilati correttamente:</div>
          {Object.entries(formErrors).map(([lang, errors]: any) => (
            <div key={lang} className={'mt-1'}>
              {lang != genericLanguage && (
                <div>
                  <strong>Lingua:</strong> <CountryFlag language={lang} />
                </div>
              )}
              {lang == genericLanguage && (
                <div>
                  <strong>Errori generici:</strong>
                </div>
              )}
              <div>
                {lang != genericLanguage && <strong>Campi:</strong>}{' '}
                {lang != genericLanguage &&
                  errors.map((err) => (
                    <Badge variant={'light'} key={err} className={'mr-1'}>
                      {err}
                    </Badge>
                  ))}
                {lang == genericLanguage && errors.map((err) => <div key={err}>&bull; {err}</div>)}
              </div>
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer>
          <Button variant={'secondary'} onClick={() => setFormErrorsModalOpen(false)}>
            Chiudi
          </Button>
        </Modal.Footer>
      </Modal>

      <SubtitleUploader
        show={subtitleModalOpen}
        onHide={() => setSubtitleModalOpen(false)}
        onConfirm={({ languageCode, file }) => {
          subtitles.current[languageCode] = file;
          detectChanges();
        }}
      />
    </>
  );
};

export default PublishComponent;
